import {ValidEmail} from 'ApplicationEntry/models/Email';
import {ValidGroupApplicantDemographics} from 'ApplicationEntry/models/GroupApplicantDemographics';
import {ValidHeight} from 'ApplicationEntry/models/Height';
import {UserAssignedOccupation} from 'ApplicationEntry/models/Occupation';
import {ValidOrdinaryApplicantDemographics} from 'ApplicationEntry/models/OrdinaryApplicantDemographics';
import {ValidOwnerInfo} from 'ApplicationEntry/models/OwnerInfo';
import {ValidPhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import {ValidSocialSecurityNumber} from 'ApplicationEntry/models/SocialSecurityNumber';
import {ValidZipCode} from 'ApplicationEntry/models/ZipCode';
import {NonEmptyString} from 'Core/models/EmptyString';
import DateUtility from 'Shared/utilities/DateUtility';

export const getOrdinaryApplicantDemographics = (
  clientApplicant: object,
  clientApplicantEmployeeModel: object,
  clientApplicantAddress: object
) => {
  const applicantName = formatName(
    clientApplicant['firstName'],
    clientApplicant['middleName'],
    clientApplicant['lastName']
  );
  const applicantType = clientApplicant['relationshipToPI'];
  const applicantSex = clientApplicant['gender'];
  const applicantEmailAddress = clientApplicant['emailAddress'];
  const applicantWeight = clientApplicant['weight'];
  const applicantSsn = clientApplicant['ssn'];
  const applicantPhoneNumber = Number(clientApplicant['workPhone']) === 0 ? '' : clientApplicant['workPhone'];
  const applicantDob = clientApplicant['dateOfBirth'];
  const applicantHireDate = clientApplicantEmployeeModel['dateOfHire'];
  const applicantAddressFirstLine = clientApplicantAddress['streetAddress1'];
  const applicantAddressSecondLine = clientApplicantAddress['streetAddress2'];
  const applicantAddressCity = clientApplicantAddress['city'];
  const applicantAddressState = clientApplicantAddress['state'];
  const applicantAddressZipCode = clientApplicantAddress['zipCode'];
  const applicantOccupation = clientApplicantEmployeeModel['occupation'];
  const applicantOccSmokerCode = clientApplicant['occSmokerCode'];
  let heightInFeet = '';
  let heightInInches = '';
  if (clientApplicant['height'] !== '0' || clientApplicant['height'] !== '') {
    heightInFeet = clientApplicant['height'].substring(0, 1);
    heightInInches = clientApplicant['height'].substring(1, 3);
  }

  return ValidOrdinaryApplicantDemographics.create(
    NonEmptyString.create(applicantName),
    applicantType === 'SELF' ? 'Ind' : '',
    applicantSex,
    new Date(DateUtility.toFormattedDateAsUtc(applicantDob)),
    NonEmptyString.create(applicantAddressFirstLine),
    NonEmptyString.create(applicantAddressSecondLine),
    NonEmptyString.create(applicantAddressCity),
    NonEmptyString.create(applicantAddressState),
    ValidZipCode.create(applicantAddressZipCode),
    ValidSocialSecurityNumber.create(applicantSsn),
    applicantHireDate === '' ? '' : new Date(applicantHireDate),
    ValidEmail.create(applicantEmailAddress),
    ValidHeight.create(Number(heightInFeet), heightInInches === '' ? 0 : Number(heightInInches)),
    applicantWeight,
    false,
    UserAssignedOccupation.create(applicantOccupation, '', '', ''),
    ValidPhoneNumber.create(applicantPhoneNumber),
    NonEmptyString.create(applicantOccSmokerCode)
  );
};

export const getGroupApplicantDemographics = (
  clientApplicant: object,
  clientApplicantEmployeeModel: object,
  clientApplicantAddress: object
) => {
  const applicantName = formatName(
    clientApplicant['firstName'],
    clientApplicant['middleName'],
    clientApplicant['lastName']
  );
  const applicantType = clientApplicant['type'];
  const applicantSex = clientApplicant['gender'];
  const applicantEmailAddress = clientApplicant['emailAddress'];
  const applicantSsn = clientApplicant['ssn'];
  const applicantPhoneNumber = Number(clientApplicant['workPhone']) === 0 ? '' : clientApplicant['workPhone'];
  const applicantDob = clientApplicant['dateOfBirth'];
  const applicantHireDate = clientApplicantEmployeeModel['dateOfHire'];
  const applicantAddressFirstLine = clientApplicantAddress['streetAddress1'];
  const applicantAddressSecondLine = clientApplicantAddress['streetAddress2'];
  const applicantAddressCity = clientApplicantAddress['city'];
  const applicantAddressState = clientApplicantAddress['state'];
  const applicantAddressZipCode = clientApplicantAddress['zipCode'];
  const applicantOccupation = clientApplicantEmployeeModel['occupation'];
  const applicantOccSmokerCode = clientApplicant['occSmokerCode'];

  return ValidGroupApplicantDemographics.create(
    NonEmptyString.create(applicantName),
    applicantType,
    applicantSex,
    new Date(DateUtility.toFormattedDateAsUtc(applicantDob)),
    NonEmptyString.create(applicantAddressFirstLine),
    NonEmptyString.create(applicantAddressSecondLine),
    NonEmptyString.create(applicantAddressCity),
    NonEmptyString.create(applicantAddressState),
    ValidZipCode.create(applicantAddressZipCode),
    ValidSocialSecurityNumber.create(applicantSsn),
    applicantHireDate === '' ? '' : new Date(applicantHireDate),
    ValidEmail.create(applicantEmailAddress),
    ValidPhoneNumber.create(applicantPhoneNumber),
    UserAssignedOccupation.create(applicantOccupation, '', '', ''),
    NonEmptyString.create(applicantOccSmokerCode)
  );
};

const formatName = (firstName: string | undefined, middleName: string | undefined, lastName: string | undefined) => {
  const firstNameTransformed = firstName !== undefined && firstName !== '' ? `,${firstName}` : '';
  const middleNameTransformed = middleName !== undefined && middleName !== '' ? ` ${middleName}` : '';
  return lastName + firstNameTransformed + middleNameTransformed;
};

export const getOwnerInfo = (
  isApplicantOwner: boolean,
  ownerIndex: number,
  clients: object[],
  clientApplicant: object,
  clientApplicantAddress: object
) => {
  let ownerName;
  let ownerSsn;
  let ownerPhoneNumber;
  let ownerDob;
  let sameAddressAsApplicant;
  let ownerAddressFirstLine;
  let ownerAddressSecondLine;
  let ownerAddressCity;
  let ownerAddressState;
  let ownerAddressZipCode;
  let ownerSigned;
  let ownerIsMailingAddress;
  let ownerOccSmokerCode;

  const applicantName = formatName(
    clientApplicant['firstName'],
    clientApplicant['middleName'],
    clientApplicant['lastName']
  );
  const applicantSsn = clientApplicant['ssn'];
  const applicantPhoneNumber = Number(clientApplicant['workPhone']) === 0 ? '' : clientApplicant['workPhone'];
  const applicantDob = clientApplicant['dateOfBirth'];
  const applicantAddressFirstLine = clientApplicantAddress['streetAddress1'];
  const applicantAddressSecondLine = clientApplicantAddress['streetAddress2'];
  const applicantAddressCity = clientApplicantAddress['city'];
  const applicantAddressState = clientApplicantAddress['state'];
  const applicantAddressZipCode = clientApplicantAddress['zipCode'];
  const applicantOwnerSigned = Boolean(clientApplicant['ownerSigned']);
  const applicantMailingAddress = Boolean(clientApplicant['useForMail']);
  const applicantOccSmokerCode = clientApplicant['occSmokerCode'];

  if (!isApplicantOwner) {
    const clientOwner = clients[ownerIndex];
    const clientOwnerAddress = clientOwner['address'];

    ownerName = formatName(clientOwner['firstName'], clientOwner['middleName'], clientOwner['lastName']);
    ownerSsn = clientOwner['ssn'];
    ownerPhoneNumber = Number(clientOwner['workPhone']) === 0 ? '' : clientOwner['workPhone'];
    ownerDob = clientOwner['dateOfBirth'];
    sameAddressAsApplicant = false;
    ownerAddressFirstLine = clientOwnerAddress['streetAddress1'];
    ownerAddressSecondLine = clientOwnerAddress['streetAddress2'];
    ownerAddressCity = clientOwnerAddress['city'];
    ownerAddressState = clientOwnerAddress['state'];
    ownerAddressZipCode = clientOwnerAddress['zipCode'];
    ownerSigned = Boolean(clientOwner['ownerSigned']);
    ownerIsMailingAddress = Boolean(clientOwner['useForMail']);
    ownerOccSmokerCode = clientOwner['occSmokerCode'];
  } else {
    ownerName = applicantName;
    ownerSsn = applicantSsn;
    ownerPhoneNumber = applicantPhoneNumber;
    ownerDob = applicantDob;
    sameAddressAsApplicant = true;
    ownerAddressFirstLine = applicantAddressFirstLine;
    ownerAddressSecondLine = applicantAddressSecondLine;
    ownerAddressCity = applicantAddressCity;
    ownerAddressState = applicantAddressState;
    ownerAddressZipCode = applicantAddressZipCode;
    ownerSigned = applicantOwnerSigned;
    ownerIsMailingAddress = applicantMailingAddress;
    ownerOccSmokerCode = applicantOccSmokerCode;
  }

  return ValidOwnerInfo.create(
    isApplicantOwner,
    NonEmptyString.create(ownerName),
    ValidPhoneNumber.create(ownerPhoneNumber),
    new Date(DateUtility.toFormattedDateAsUtc(ownerDob)),
    ValidSocialSecurityNumber.create(ownerSsn),
    sameAddressAsApplicant,
    NonEmptyString.create(ownerAddressFirstLine),
    NonEmptyString.create(ownerAddressSecondLine),
    NonEmptyString.create(ownerAddressCity),
    NonEmptyString.create(ownerAddressState),
    ValidZipCode.create(ownerAddressZipCode),
    ownerSigned,
    ownerIsMailingAddress,
    NonEmptyString.create(ownerOccSmokerCode)
  );
};
